import CrudSection from "../components/CrudSection";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Reglas y auditoría — bespoke sections (mock data, interactive in-session).
// Mirrors the landing "Reglas y auditoría" feature: permisos por horario /
// rol / zona / tipo de visitante, listas negras, anti-passback y bitácora
// completa con reportes de cada acceso.
// ===========================================================================

export function ReglasYAuditoriaDashboard() {
  return (
    <FeatureDashboard noticeKey="nuvexAccess.mockNotice"
      title="Reglas y auditoría"
      subtitle="Reglas de acceso, listas negras, anti-passback y trazabilidad"
      icon="ri-shield-keyhole-line"
      stats={[
        { label: "Reglas activas", value: "16", icon: "ri-list-settings-line", change: "+1 esta semana", positive: true },
        { label: "En lista negra", value: "9", icon: "ri-forbid-line" },
        { label: "Eventos auditados (hoy)", value: "1,284", icon: "ri-file-list-3-line", change: "+8.4%", positive: true },
        { label: "Intentos sospechosos", value: "7", icon: "ri-alarm-warning-line", change: "-2", positive: true },
      ]}
      chart={{
        title: "Accesos por resultado (hoy)",
        data: [
          { name: "Concedidos", value: 1208 }, { name: "Denegados", value: 64 },
          { name: "Fuera de horario", value: 21 }, { name: "Lista negra", value: 5 },
          { name: "Anti-passback", value: 9 }, { name: "Sospechosos", value: 7 },
        ],
      }}
      recent={{
        title: "Eventos de auditoría recientes",
        items: [
          { primary: "Acceso denegado — fuera de horario", secondary: "Proveedor · Caseta · 22:14", badge: "Regla", tone: "warn" },
          { primary: "Bloqueo por lista negra", secondary: "Tarjeta #8821 · Torniquete A", badge: "Lista negra", tone: "danger" },
          { primary: "Anti-passback activado", secondary: "Misma credencial reutilizada · 10:02", badge: "Anti-passback", tone: "danger" },
          { primary: "Intento sospechoso", secondary: "3 intentos fallidos · Acceso norte", badge: "Sospechoso", tone: "warn" },
        ],
      }}
    />
  );
}

// --- Reglas de acceso ------------------------------------------------------
interface Regla extends Record<string, unknown> {
  id: string; nombre: string; criterio: string; condicion: string; accion: string; estado: string;
}
const reglaColumns: CrudColumn<Regla>[] = [
  { key: "nombre", label: "Regla" },
  { key: "criterio", label: "Criterio" },
  { key: "condicion", label: "Condición" },
  { key: "accion", label: "Acción", align: "center", render: (r) => <StatusTextBadge value={r.accion} /> },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const reglaFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Proveedores solo en horario laboral" },
  { key: "criterio", label: "Criterio", type: "select", required: true, defaultValue: "Horario", options: [
    { value: "Horario", label: "Horario" }, { value: "Rol", label: "Rol" },
    { value: "Zona", label: "Zona" }, { value: "Tipo de visitante", label: "Tipo de visitante" },
  ] },
  { key: "condicion", label: "Condición", required: true, placeholder: "08:00–18:00 / Residente / Lobby" },
  { key: "accion", label: "Acción", type: "select", required: true, defaultValue: "Permitir", options: [
    { value: "Permitir", label: "Permitir" }, { value: "Denegar", label: "Denegar" },
  ] },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activa", options: [
    { value: "Activa", label: "Activa" }, { value: "Inactiva", label: "Inactiva" },
  ] },
];
const reglaSeed: Regla[] = [
  { id: "rg1", nombre: "Proveedores en horario laboral", criterio: "Horario", condicion: "08:00–18:00", accion: "Permitir", estado: "Activa" },
  { id: "rg2", nombre: "Visitantes sin acceso a azotea", criterio: "Zona", condicion: "Azotea", accion: "Denegar", estado: "Activa" },
  { id: "rg3", nombre: "Residentes acceso total", criterio: "Rol", condicion: "Residente", accion: "Permitir", estado: "Activa" },
  { id: "rg4", nombre: "Repartidores solo recepción", criterio: "Tipo de visitante", condicion: "Repartidor → Recepción", accion: "Permitir", estado: "Inactiva" },
];
export const ReglasSection = () => (
  <CrudSection<Regla> title="Reglas de acceso" subtitle="Permisos por horario, rol, zona o tipo de visitante"
    icon="ri-list-settings-line" columns={reglaColumns} fields={reglaFields} seed={reglaSeed} searchKey="nombre" addLabel="Crear regla" />
);

// --- Listas negras ---------------------------------------------------------
interface ListaNegra extends Record<string, unknown> {
  id: string; tipo: string; identificador: string; motivo: string; fecha: string; estado: string;
}
const listaColumns: CrudColumn<ListaNegra>[] = [
  { key: "tipo", label: "Tipo" },
  { key: "identificador", label: "Identificador" },
  { key: "motivo", label: "Motivo" },
  { key: "fecha", label: "Desde" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const listaFields: CrudField[] = [
  { key: "tipo", label: "Tipo", type: "select", required: true, defaultValue: "Persona", options: [
    { value: "Persona", label: "Persona" }, { value: "Vehículo", label: "Vehículo" }, { value: "Credencial", label: "Credencial" },
  ] },
  { key: "identificador", label: "Identificador", required: true, placeholder: "Nombre / placa / # de tarjeta" },
  { key: "motivo", label: "Motivo", type: "textarea", required: true, placeholder: "Motivo del bloqueo" },
  { key: "fecha", label: "Fecha de alta", type: "date", required: true },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Bloqueado", options: [
    { value: "Bloqueado", label: "Bloqueado" }, { value: "Revisado", label: "Revisado" },
  ] },
];
const listaSeed: ListaNegra[] = [
  { id: "ln1", tipo: "Credencial", identificador: "Tarjeta #8821", motivo: "Tarjeta reportada como perdida", fecha: "2026-06-01", estado: "Bloqueado" },
  { id: "ln2", tipo: "Vehículo", identificador: "Placa ABC-123", motivo: "Vehículo no autorizado reincidente", fecha: "2026-05-28", estado: "Bloqueado" },
  { id: "ln3", tipo: "Persona", identificador: "Ex-proveedor X", motivo: "Contrato terminado", fecha: "2026-05-20", estado: "Revisado" },
];
export const ListasNegrasSection = () => (
  <CrudSection<ListaNegra> title="Listas negras" subtitle="Personas, vehículos y credenciales bloqueadas"
    icon="ri-forbid-line" columns={listaColumns} fields={listaFields} seed={listaSeed} searchKey="identificador" addLabel="Agregar a lista negra" />
);

// --- Anti-passback ---------------------------------------------------------
interface AntiPassback extends Record<string, unknown> {
  id: string; zona: string; modo: string; ventana: string; estado: string;
}
const apbColumns: CrudColumn<AntiPassback>[] = [
  { key: "zona", label: "Zona" },
  { key: "modo", label: "Modo" },
  { key: "ventana", label: "Ventana de tiempo" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const apbFields: CrudField[] = [
  { key: "zona", label: "Zona", required: true, placeholder: "Estacionamiento / Torniquetes" },
  { key: "modo", label: "Modo", type: "select", required: true, defaultValue: "Estricto", options: [
    { value: "Estricto", label: "Estricto (bloquea reingreso)" }, { value: "Suave", label: "Suave (solo registra)" },
  ] },
  { key: "ventana", label: "Ventana de tiempo", required: true, placeholder: "15 minutos / Hasta marcar salida" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Inactivo", label: "Inactivo" },
  ] },
];
const apbSeed: AntiPassback[] = [
  { id: "ap1", zona: "Estacionamiento", modo: "Estricto", ventana: "Hasta marcar salida", estado: "Activo" },
  { id: "ap2", zona: "Torniquetes lobby", modo: "Estricto", ventana: "15 minutos", estado: "Activo" },
  { id: "ap3", zona: "Amenidades", modo: "Suave", ventana: "30 minutos", estado: "Inactivo" },
];
export const AntiPassbackSection = () => (
  <CrudSection<AntiPassback> title="Anti-passback" subtitle="Evita la reutilización de una credencial para colar a varias personas"
    icon="ri-swap-box-line" columns={apbColumns} fields={apbFields} seed={apbSeed} searchKey="zona" addLabel="Configurar zona" />
);

// --- Bitácora de auditoría (readOnly log) ----------------------------------
interface Auditoria extends Record<string, unknown> {
  id: string; fecha: string; usuario: string; zona: string; accion: string; resultado: string;
}
const auditoriaColumns: CrudColumn<Auditoria>[] = [
  { key: "fecha", label: "Fecha y hora" },
  { key: "usuario", label: "Usuario / credencial" },
  { key: "zona", label: "Zona" },
  { key: "accion", label: "Acción" },
  { key: "resultado", label: "Resultado", align: "center", render: (r) => <StatusTextBadge value={r.resultado} /> },
];
const auditoriaSeed: Auditoria[] = [
  { id: "au1", fecha: "2026-06-08 10:32", usuario: "Juan Pérez", zona: "Caseta principal", accion: "Entrada", resultado: "Concedido" },
  { id: "au2", fecha: "2026-06-08 10:28", usuario: "Tarjeta #8821", zona: "Torniquete A", accion: "Entrada", resultado: "Lista negra" },
  { id: "au3", fecha: "2026-06-08 10:02", usuario: "Credencial 4471", zona: "Estacionamiento", accion: "Reingreso", resultado: "Anti-passback" },
  { id: "au4", fecha: "2026-06-07 22:14", usuario: "Proveedor", zona: "Caseta principal", accion: "Entrada", resultado: "Fuera de horario" },
];
export const AuditoriaSection = () => (
  <CrudSection<Auditoria> title="Bitácora de auditoría" subtitle="Trazabilidad completa de entradas, salidas y rechazos"
    icon="ri-file-list-3-line" columns={auditoriaColumns} fields={[]} seed={auditoriaSeed} searchKey="usuario" readOnly />
);

// --- Intentos sospechosos (readOnly) ---------------------------------------
interface Sospechoso extends Record<string, unknown> {
  id: string; fecha: string; punto: string; detalle: string; severidad: string;
}
const sospechosoColumns: CrudColumn<Sospechoso>[] = [
  { key: "fecha", label: "Fecha y hora" },
  { key: "punto", label: "Punto de acceso" },
  { key: "detalle", label: "Detalle" },
  { key: "severidad", label: "Severidad", align: "center", render: (r) => <StatusTextBadge value={r.severidad} /> },
];
const sospechosoSeed: Sospechoso[] = [
  { id: "s1", fecha: "2026-06-08 03:18", punto: "Acceso norte", detalle: "3 intentos fallidos consecutivos", severidad: "Alta" },
  { id: "s2", fecha: "2026-06-08 01:42", punto: "Estacionamiento", detalle: "Forzado de pluma detectado", severidad: "Alta" },
  { id: "s3", fecha: "2026-06-07 23:55", punto: "Torniquete A", detalle: "Credencial fuera de horario", severidad: "Media" },
  { id: "s4", fecha: "2026-06-07 20:11", punto: "Caseta principal", detalle: "Tailgating (paso doble) detectado", severidad: "Media" },
];
export const SospechososSection = () => (
  <CrudSection<Sospechoso> title="Intentos sospechosos" subtitle="Accesos no autorizados e intentos fallidos detectados"
    icon="ri-alarm-warning-line" columns={sospechosoColumns} fields={[]} seed={sospechosoSeed} searchKey="punto" readOnly />
);
